import {
  CalendarIcon,
  ChevronDownIcon,
  DocumentMagnifyingGlassIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { ChevronRight } from 'lucide-react';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { BaseTag } from '@/components/chariot-ui';
import { SoundRecognitionSearch as SoundRecognitionSearchIcon } from '@/components/chariot-ui';
import { CopyToClipboard } from '@/components/CopyToClipboard';
import { Container } from '@/components/legacy-ui';
import { NoData } from '@/components/legacy-ui/NoData';
import { Loader } from '@/components/Loader';
import { MarkdownPreview } from '@/components/markdown/MarkdownPreview';
import { Modal } from '@/components/Modal';
import Tabs from '@/components/Tabs';
import { TimelineEvent } from '@/components/TimelineEvent';
import { Tooltip } from '@/components/Tooltip';
import { Button } from '@/components/ui/button';
import { useGetFile } from '@/hooks/useFiles';
import { useGenericSearch } from '@/hooks/useGenericSearch';
import { AllIntegrations } from '@/hooks/useIntegration';
import { parseKeys } from '@/sections/SearchByType';
import { useGlobalState } from '@/state/global.state';
import { Asset } from '@/types';
import { ResourceType, Risk } from '@/types';
import { Integration } from '@/types';
import { cn } from '@/utils/classname';
import { formatDate } from '@/utils/date.util';
import { useStorage } from '@/utils/storage/useStorage.util';

const DEFAULT_LIST_ITEM_LIMIT = 10;

interface IntegrationAffiliationResult {
  integration: string;
  parent: string;
  child: string;
  determination: 'MATCH' | 'NO MATCH' | 'UNKNOWN';
  error: string;
}

interface AffiliationData {
  danglingDnsCheck: {
    cloudInstanceDanglingDnsCheck: string;
    evidence: string;
    danglingDnsAgent:
      | 'CONNECTED'
      | 'UNRELATED'
      | 'UNKNOWN'
      | 'UNCERTAIN'
      | string;
    danglingDnsAgentEvidence: string;
  };
  dangling_dns_record_result: string;
  chariot_asset_affiliation_result: boolean;
  chariotBasedAssetAffiliation: {
    assetOrigin: {
      assetIsProvided?: {
        value: string;
        originator: string;
      };
      assetIsSeed: {
        value: string;
        originator: string;
      };
      parentAssetIsProvided?: {
        value: string;
        originator: string;
      };
      parentAssetIsCloudAsset: {
        value: string;
        originator: string;
      };
    };
    topLevelDomain: {
      originator: string;
      value: string;
    };
    relationships: {
      risksAssociatedWithTld: {
        value: string;
        originator: string;
      }[];
      risksAssociatedWithIp: {
        value: string;
        originator: string;
      }[];
    };
    openAssociatedRisks: {
      value: string;
      originator: string;
    }[];
  };
  ownershipVerification: {
    whois: OwnershipEvidence;
    ssl: OwnershipEvidence & { short_evidence_per_certificate?: string[] };
    dns: OwnershipEvidence;
    websearch: OwnershipEvidence;
    verifier: OwnershipEvidence;
  };
  finalVerdict:
    | 'Client owns domain'
    | 'Client does not own domain'
    | 'Uncertain'
    | 'uncertain'
    | string;
  ssl_agent_result?: OwnershipEvidence & {
    notes?: string[];
    errors?: string[];
    warnings?: string[];
    short_evidence_per_certificate?: string[];
  };
  whois_agent_result?: OwnershipEvidence & {
    notes?: string[];
    errors?: string[];
    warnings?: string[];
  };
  dns_agent_result?: OwnershipEvidence & {
    notes?: string[];
    errors?: string[];
    warnings?: string[];
  };
  websearch_agent_result?: OwnershipEvidence & {
    notes?: string[];
    errors?: string[];
    warnings?: string[];
  };
  verifier_agent_result?: OwnershipEvidence & {
    notes?: string[];
    errors?: string[];
    warnings?: string[];
  };
  ownershipVerificationV1?: string;
  integrationAffiliationResults?: IntegrationAffiliationResult[];
  chariotAffiliationSkipped?: boolean;
}

interface OwnershipEvidence {
  long_evidence: string;
  short_evidence: string;
  outcome:
    | 'CONFIRMED'
    | 'LIKELY CONFIRMED'
    | 'UNCERTAIN'
    | 'UNKNOWN'
    | 'UNSUPPORTED'
    | 'ERROR'
    | 'MISMATCH'
    | string;
}

interface StatusChipProps {
  value: string;
  colorMap: Record<string, string>;
  defaultClassName?: string;
}

function StatusChip({ value, colorMap, defaultClassName }: StatusChipProps) {
  const className =
    colorMap[value.toLowerCase()] ||
    defaultClassName ||
    'border-chariot-stroke-low bg-chariot-status-low';

  return (
    <BaseTag className={className} size="sm">
      {value?.toUpperCase()}
    </BaseTag>
  );
}

const chariotDanglingDnsColorMap: Record<string, string> = {
  unlikely: 'border-chariot-stroke-info bg-chariot-status-info',
  na: 'border-chariot-stroke-low bg-chariot-status-low',
  likely: 'border-chariot-stroke-critical bg-chariot-status-critical',
};

const chariotAffiliationColorMap: Record<string, string> = {
  likely: 'border-chariot-stroke-info bg-chariot-status-info',
  uncertain: 'border-chariot-stroke-low bg-chariot-status-low',
};

function CertificatesContainer({ certificates }: { certificates: string[] }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [sslTab, setSslTab] = useState('0');

  const tabs = (certificates || []).map((evidence, idx) => {
    const match = evidence.match(/https?:\/\/[^:]+:(\d+)/);
    const label = match ? `Port ${match[1]}` : `Cert ${idx + 1}`;
    return {
      label,
      value: String(idx),
      content: (
        <div
          className="prose prose-sm text-chariot-text-primary break-words"
          data-testid="modal-body"
        >
          <MarkdownPreview source={evidence} />
        </div>
      ),
    };
  });

  // Extract hostname from the first certificate evidence string
  const selectedCert = certificates[Number(sslTab)] ?? 0;
  const hostnameMatch = selectedCert.match(/https?:\/\/([^:/]+)/);
  const portMatch = selectedCert.match(/:(\d+)/);
  const hostnameAndPort = hostnameMatch
    ? `${hostnameMatch[1]}:${portMatch ? portMatch[1] : ''}`
    : 'Certificate Host';

  return (
    <>
      <ul className="mt-2 flex flex-col gap-1 text-xs">
        {certificates.map((evidence, idx) => {
          let displayPart = evidence;
          const urlWithPortMatch = evidence.match(/^(https?:\/\/[^:]+:\d+):/);
          if (urlWithPortMatch && urlWithPortMatch[1]) {
            displayPart = urlWithPortMatch[1];
          }
          return (
            <li key={idx} className="flex items-center gap-2">
              <button
                onClick={() => {
                  setSslTab(String(idx));
                  setModalOpen(true);
                }}
                className="text-chariot-text-primary flex items-center underline"
              >
                <DocumentMagnifyingGlassIcon className="size-4" />
                <p className="ml-1 truncate">{displayPart}</p>
                <ChevronRight className="size-4" />
              </button>
            </li>
          );
        })}
      </ul>
      {modalOpen && (
        <Modal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          title={
            <div className="flex items-center gap-2">
              <DocumentMagnifyingGlassIcon className="size-4" />
              {hostnameAndPort}
            </div>
          }
          size="3xl"
          hideFooter
        >
          <div className="flex w-full gap-4">
            <div className="shrink-0 overflow-auto">
              <Tabs
                id="ssl-cert-tabs"
                vertical
                variant="secondary"
                tabs={tabs}
                selected={tabs.find(tab => tab.value === sslTab) || tabs[0]}
                onSelect={tab => setSslTab(tab.value)}
                align="left"
                className="w-41 [&>button]:capitalize"
              />
            </div>
            <div className="border-chariot-stroke w-1 border-l" />

            <div className="flex-1 overflow-auto">
              {tabs.find(tab => tab.value === sslTab)?.content}
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

export function AssetAffiliation(props: { asset: Asset }) {
  const [expandedContainer, setExpandedContainer] = useStorage(
    { queryKey: 'historyId' },
    0
  );
  const assetAffiliations = getAssetAffiliations({ asset: props.asset });

  const recommendationFilePath =
    assetAffiliations[expandedContainer]?.filePath || '';

  const { data: recommendation, status: recommendationFileStatus } = useGetFile(
    { name: recommendationFilePath },
    { enabled: Boolean(recommendationFilePath) }
  );

  useEffect(() => {
    if (
      assetAffiliations.length > expandedContainer &&
      recommendationFileStatus === 'success'
    ) {
      const element = document.getElementById(
        `asset-affiliation-${expandedContainer}`
      );
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [expandedContainer, assetAffiliations.length, recommendationFileStatus]);

  useEffect(() => {
    setExpandedContainer(0);
  }, [props.asset.key, setExpandedContainer]);

  if (assetAffiliations.length === 0) {
    return (
      <Container
        className="size-full overflow-auto p-6"
        tabIndex={-1}
        id="history"
      >
        <div className="flex h-full flex-col">
          <NoData
            icon={<CalendarIcon className="text-chariot-stroke size-[100px]" />}
            title="No History Has Been Added"
          />
        </div>
      </Container>
    );
  }

  return (
    <Container
      className="size-full overflow-auto p-6"
      tabIndex={-1}
      id="history"
    >
      <div className="flex flex-col items-center gap-8">
        {assetAffiliations.map((assetAffiliation, index) => {
          const isSelected = expandedContainer === index;
          const isPreviousSelected = expandedContainer === index - 1;

          return (
            <TimelineEvent
              key={index}
              index={index}
              isSelected={isSelected}
              isPreviousSelected={isPreviousSelected}
            >
              {/* Accordian title */}
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-lg font-bold">Customer Affiliation</h1>
                  <FinalVerdict
                    value={
                      (assetAffiliation.affiliationVerdict as unknown as AffiliationData['finalVerdict']) ||
                      'uncertain'
                    }
                    renderActualText
                  />
                </div>
                <p className="text-chariot-text-secondary text-sm">
                  {formatDate(assetAffiliation.updated)}
                </p>
              </div>
              {/* Accordian Body */}
              {isSelected && (
                <Loader
                  className="mt-4"
                  isLoading={recommendationFileStatus === 'pending'}
                >
                  {recommendation && (
                    <AssetAffiliationContainer
                      recommendation={{
                        ...recommendation,
                      }}
                    />
                  )}
                  {!recommendation && (
                    <NoData
                      icon={
                        <SoundRecognitionSearchIcon className="text-chariot-stroke text-[100px]" />
                      }
                      title="Recommendation File Not Found"
                    />
                  )}
                </Loader>
              )}
              {/* Accordian expand button */}
              {!isSelected && (
                <Button
                  className="mt-4 rounded-full"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setExpandedContainer(index);
                  }}
                >
                  View Details
                  <ChevronDownIcon className="size-4" />
                </Button>
              )}
            </TimelineEvent>
          );
        })}
      </div>
    </Container>
  );
}

function AssetAffiliationContainer(props: { recommendation: AffiliationData }) {
  const {
    chariotBasedAssetAffiliation,
    ownershipVerification,
    danglingDnsCheck,
  } = props.recommendation;

  const { drawerState } = useGlobalState();
  const [riskDetailsToLoad, setRiskDetailsToLoad] = useState<string | null>(
    null
  );
  const processedRiskKeyRef = useRef<string | null>(null);

  const riskDetailsQuery = useGenericSearch(
    {
      query: riskDetailsToLoad!,
      label: ResourceType.Risk,
      exact: true,
    },
    {
      enabled: !!riskDetailsToLoad,
    }
  );

  // Handle query status changes with useEffect
  // Use ref to track processed queries to avoid cascading setState
  useEffect(() => {
    // Skip if we already processed this query
    if (processedRiskKeyRef.current === riskDetailsToLoad) return;

    if (
      riskDetailsQuery.status === 'success' &&
      riskDetailsQuery.data &&
      riskDetailsToLoad
    ) {
      processedRiskKeyRef.current = riskDetailsToLoad;
      const risks = riskDetailsQuery.data.risks || [];
      if (risks.length > 0) {
        drawerState.handleOpenVulnerabilityDrawer(risks[0] as Risk);
      } else {
        toast.error('Risk details not found.');
      }
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setRiskDetailsToLoad(null);
    } else if (riskDetailsQuery.status === 'error' && riskDetailsToLoad) {
      processedRiskKeyRef.current = riskDetailsToLoad;
      toast.error('Failed to load risk details.');
      setRiskDetailsToLoad(null);
    }
  }, [
    riskDetailsQuery.status,
    riskDetailsQuery.data,
    riskDetailsToLoad,
    drawerState,
  ]);

  const ownership = ownershipVerification;

  const assetOriginDetails = chariotBasedAssetAffiliation?.assetOrigin;

  const certificatesToDisplay =
    props.recommendation.ownershipVerification?.ssl
      ?.short_evidence_per_certificate ||
    props.recommendation.ssl_agent_result?.short_evidence_per_certificate;

  interface AssetOriginListItem {
    text: string;
    isDefault: boolean;
    originator?: string;
  }

  const assetOriginItems: AssetOriginListItem[] = [];

  // Determine Seed Status item
  let seedStatusText = 'Not a Seed Asset'; // Default
  let seedStatusIsDefault = true;
  if (
    assetOriginDetails?.assetIsSeed?.value &&
    assetOriginDetails.assetIsSeed.value.trim() !== ''
  ) {
    seedStatusText = assetOriginDetails.assetIsSeed.value;
    seedStatusIsDefault = false;
  }
  assetOriginItems.push({
    text: seedStatusText,
    isDefault: seedStatusIsDefault,
  });

  // Determine Parent Cloud Asset Status item
  let parentCloudStatusText = 'Parent asset is not a cloud asset'; // Default
  let parentCloudIsDefault = true;
  let parentCloudOriginator: string | undefined;
  if (
    assetOriginDetails?.parentAssetIsCloudAsset?.value &&
    assetOriginDetails.parentAssetIsCloudAsset.value.trim() !== ''
  ) {
    parentCloudStatusText = assetOriginDetails.parentAssetIsCloudAsset.value;
    parentCloudIsDefault = false;
    parentCloudOriginator =
      assetOriginDetails.parentAssetIsCloudAsset.originator;
  }
  assetOriginItems.push({
    text: parentCloudStatusText,
    isDefault: parentCloudIsDefault,
    originator: parentCloudOriginator,
  });

  const risksAssociatedWithTld =
    chariotBasedAssetAffiliation.relationships.risksAssociatedWithTld;
  const risksAssociatedWithIp =
    chariotBasedAssetAffiliation.relationships.risksAssociatedWithIp;
  const openAssociatedRisks =
    chariotBasedAssetAffiliation.openAssociatedRisks || [];

  const handleRiskItemClick = (originator: string, value: string) => {
    const riskKey = `#risk#${originator}#${value}`;

    setRiskDetailsToLoad(riskKey);
  };

  return (
    <div className="flex flex-col pt-5">
      <H2
        label="Dangling DNS"
        headingElement={
          <StatusChip
            value={danglingDnsCheck.cloudInstanceDanglingDnsCheck}
            colorMap={chariotDanglingDnsColorMap}
          />
        }
      >
        <H4 label="Praetorian">{danglingDnsCheck.evidence || '-'}</H4>
        <H4 label="Web Search">
          {danglingDnsCheck.danglingDnsAgentEvidence || '-'}
        </H4>
      </H2>
      {props.recommendation.integrationAffiliationResults &&
        props.recommendation.integrationAffiliationResults.length > 0 &&
        (() => {
          // Determine chip status
          let results = props.recommendation.integrationAffiliationResults;
          // Sort: MATCH first, then NO MATCH, then UNKNOWN
          results = [...results].sort((a, b) => {
            const order = { MATCH: 0, 'NO MATCH': 1, UNKNOWN: 2 };
            return (
              (order[a.determination] ?? 3) - (order[b.determination] ?? 3)
            );
          });
          let chipType: 'confirmed' | 'unknown' | 'mismatch' = 'unknown';
          if (results.some(r => r.determination === 'MATCH')) {
            chipType = 'confirmed';
          } else if (results.every(r => r.determination === 'NO MATCH')) {
            chipType = 'mismatch';
          } else if (results.every(r => r.determination === 'UNKNOWN')) {
            chipType = 'unknown';
          }
          return (
            <H2
              label="Integration Verification"
              headingElement={
                chipType === 'confirmed' ? (
                  <FinalDetermination value="PRESENT" />
                ) : chipType === 'unknown' ? (
                  <FinalDetermination value="NOT PRESENT" />
                ) : (
                  <span className="bg-chariot-status-warning border-chariot-stroke-warning text-chariot-text-primary w-fit rounded-full border px-2 py-1 text-xs font-bold">
                    MISMATCH
                  </span>
                )
              }
            >
              <div>
                <div className="border-chariot-stroke rounded-md border">
                  <div className="flex p-3 text-xs font-bold">
                    <div className="w-52">Enumerated from Integration</div>
                    <div className="w-52">Integration</div>
                    <div className="flex-1">Identifier</div>
                    <div className="flex-1">Asset</div>
                  </div>
                  {results.map((row, idx) => {
                    const integrationMeta =
                      AllIntegrations[row.integration as Integration];

                    // Remove all color from match column
                    const statusClass = 'text-chariot-text-primary';
                    // Clean identifier: remove '#asset#integration#' prefix
                    let identifier = row.parent;
                    let child = row.child;
                    const assetPrefix = `#asset#${row.integration}#`;
                    if (identifier.startsWith(assetPrefix)) {
                      identifier = identifier.slice(assetPrefix.length);
                    }
                    if (child && child.startsWith(assetPrefix)) {
                      child = child.slice(assetPrefix.length);
                    }

                    function mapDetermination(determination: string) {
                      switch (determination) {
                        case 'MATCH':
                          return 'Yes';
                        case 'NO MATCH':
                          return 'No';
                        case 'UNKNOWN':
                          return 'Unknown';
                        default:
                          return determination;
                      }
                    }
                    return (
                      <div
                        key={idx}
                        className="border-chariot-stroke flex items-start border-t p-3 text-xs"
                      >
                        <div className={`w-52 font-medium ${statusClass}`}>
                          {mapDetermination(row.determination)}
                        </div>
                        <div className="flex w-52 items-center gap-2">
                          <span className="capitalize">
                            {integrationMeta?.name || row.integration}
                          </span>
                        </div>
                        <div className="flex-1">
                          <div className="flex flex-row items-center gap-2">
                            {row.error && (
                              <div>
                                <Tooltip title={row.error}>
                                  <ExclamationTriangleIcon className="text-chariot-text-secondary size-4" />
                                </Tooltip>
                              </div>
                            )}
                            <p>{identifier}</p>
                          </div>
                        </div>
                        <div className="flex flex-1 flex-col">
                          <button
                            className="text-chariot-brand-primary text-left text-xs font-bold hover:underline"
                            onClick={() => {
                              const { group, identifier } = parseKeys.assetKey(
                                row.child
                              );
                              drawerState.setAssetDrawerTab('');
                              drawerState.handleOpenAssetDrawer({
                                key: row.child,
                                identifier,
                                group,
                                visited: '',
                              });
                            }}
                            type="button"
                          >
                            {child.split('#')[3]}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </H2>
          );
        })()}
      {!props.recommendation.chariotAffiliationSkipped && (
        <H2
          label="Guard Affiliation"
          headingElement={
            <StatusChip
              value={
                props.recommendation.chariot_asset_affiliation_result
                  ? 'Likely'
                  : 'Uncertain'
              }
              colorMap={chariotAffiliationColorMap}
            />
          }
        >
          <H3 label="Asset Origin">
            <ul className="flex flex-col gap-1 pl-2">
              {assetOriginItems.map((item, index) => (
                <li key={index} className="flex items-center gap-2 text-xs">
                  <div className="bg-chariot-stroke size-1.5 shrink-0 rounded-full"></div>
                  <span className={item.text.includes('not') ? 'italic' : ''}>
                    {item.text.toLowerCase()}
                  </span>
                  {item.originator && !item.isDefault && (
                    <CopyTag label={item.originator} />
                  )}
                </li>
              ))}
            </ul>
          </H3>
          <H3 label="Top-level domain">
            <ul className="flex flex-col gap-1 pl-2">
              <li className="flex items-center gap-2 text-xs">
                <div className="bg-chariot-stroke size-1.5 shrink-0 rounded-full"></div>
                <span
                  className={
                    chariotBasedAssetAffiliation.topLevelDomain?.value
                      ?.toLowerCase()
                      .includes('not')
                      ? 'italic'
                      : ''
                  }
                >
                  {chariotBasedAssetAffiliation.topLevelDomain?.value}
                </span>
                {chariotBasedAssetAffiliation.topLevelDomain?.originator ? (
                  <CopyTag
                    label={
                      chariotBasedAssetAffiliation.topLevelDomain.originator
                    }
                  />
                ) : (
                  <span>-</span>
                )}
              </li>
            </ul>
          </H3>
          <H3 label="Relationships">
            <div className="flex flex-col">
              <div className="mt-2">
                <List
                  label="Open Associated Risks"
                  options={openAssociatedRisks.map(r => ({
                    ...r,
                    label: r.value,
                  }))}
                  onItemClick={handleRiskItemClick}
                />
              </div>
              <div className="pt-4">
                {risksAssociatedWithTld.length > 0 ? (
                  <List
                    label="Risks Associated with Top-Level Domain"
                    options={risksAssociatedWithTld.map(r => ({
                      ...r,
                      label: r.value,
                    }))}
                    onItemClick={handleRiskItemClick}
                  />
                ) : (
                  <List
                    label="Risks associated with parent CIDR"
                    options={risksAssociatedWithIp.map(r => ({
                      ...r,
                      label: r.value,
                    }))}
                  />
                )}
              </div>
            </div>
          </H3>
        </H2>
      )}
      <H2
        label="LLM Affiliation"
        headingElement={
          <FinalDetermination
            value={
              props.recommendation.ownershipVerification?.verifier?.outcome ||
              'UNCERTAIN'
            }
          />
        }
      >
        <H3 label="Ownership Details">
          <div className="flex flex-col pt-1 [&>div]:py-2 [&>div:first-child]:pt-0 [&>div:last-child]:pb-0">
            {props.recommendation.ownershipVerification ? (
              <div className="[&>div:not(:last-child)]:border-chariot-stroke flex flex-col gap-4 [&>div:not(:last-child)]:border-b [&>div:not(:last-child)]:pb-4">
                <H4 label="WHOIS" tooltip={ownership.whois.long_evidence}>
                  {ownership.whois.short_evidence || '-'}{' '}
                  {ownership.whois.outcome}
                </H4>
                <H4
                  label="SSL Certificate"
                  tooltip={ownership.ssl.long_evidence}
                  footer={
                    certificatesToDisplay &&
                    certificatesToDisplay.length > 0 && (
                      <CertificatesContainer
                        certificates={certificatesToDisplay}
                      />
                    )
                  }
                >
                  {ownership.ssl.short_evidence || '-'} {ownership.ssl.outcome}
                </H4>
                <H4 label="DNS Records" tooltip={ownership.dns.long_evidence}>
                  {ownership.dns.short_evidence || '-'} {ownership.dns.outcome}
                </H4>
                <H4
                  label="Web Search"
                  tooltip={ownership.websearch.long_evidence}
                >
                  {ownership.websearch.short_evidence || '-'}{' '}
                  {ownership.websearch.outcome}
                </H4>
              </div>
            ) : (
              <H4>No ownership details available.</H4>
            )}
          </div>
        </H3>
      </H2>
      <H2
        label="Final Verdict"
        headingElement={
          <FinalVerdict
            value={props.recommendation.finalVerdict}
            renderActualText
          />
        }
      ></H2>
    </div>
  );
}

export function FinalVerdict(props: {
  value: AffiliationData['finalVerdict'];
  className?: string;
  renderActualText?: boolean;
}) {
  const valueMeta =
    finalVerdictMap[
      props.value.toLowerCase() as keyof typeof finalVerdictMap
    ] || finalVerdictMap.uncertain;

  return (
    <div
      className={cn(
        'w-fit rounded-full border px-2 py-1 text-xs font-bold',
        valueMeta.className,
        props.className
      )}
    >
      {props.renderActualText
        ? props.value?.toUpperCase()
        : valueMeta.name?.toUpperCase()}
    </div>
  );
}

const finalVerdictMap: Record<string, { name: string; className: string }> = {
  affiliated: {
    name: 'Affiliated',
    className: 'bg-chariot-status-info border-chariot-stroke-info',
  },
  'unaffiliated - dangling dns': {
    name: 'Unaffiliated - Dangling DNS',
    className: 'bg-chariot-status-critical border-chariot-stroke-critical',
  },
  unaffiliated: {
    name: 'Unaffiliated',
    className: 'bg-chariot-status-high border-chariot-stroke-high',
  },
  uncertain: {
    name: 'Uncertain',
    className: 'bg-chariot-status-low border-chariot-stroke-low',
  },
  // Legacy values for backward compatibility
  'client owns domain': {
    name: 'Affiliated',
    className: 'bg-chariot-status-info border-chariot-stroke-info',
  },
  'client does not own domain': {
    name: 'Unaffiliated',
    className: 'bg-chariot-status-high border-chariot-stroke-high',
  },
};

function FinalDetermination(props: {
  value: AffiliationData['ownershipVerification']['verifier']['outcome'];
}) {
  const valueMeta =
    finalDeterminationMap[
      props.value.toLowerCase() as keyof typeof finalDeterminationMap
    ] || finalDeterminationMap.uncertain;

  return (
    <div
      className={cn(
        'w-fit rounded-full border px-2 py-1 text-xs font-bold',
        valueMeta.className
      )}
    >
      {valueMeta.name?.toUpperCase()}
    </div>
  );
}

const finalDeterminationMap: Record<
  Lowercase<AffiliationData['ownershipVerification']['verifier']['outcome']>,
  { name: string; className: string }
> = {
  confirmed: {
    name: 'Confirmed',
    className: 'bg-chariot-status-info border-chariot-stroke-info',
  },
  'likely confirmed': {
    name: 'Likely Confirmed',
    className: 'bg-chariot-status-info border-chariot-stroke-info',
  },
  uncertain: {
    name: 'Uncertain',
    className: 'bg-chariot-status-low border-chariot-stroke-low',
  },
  unknown: {
    name: 'Unknown',
    className: 'bg-chariot-status-low border-chariot-stroke-low',
  },
  unsupported: {
    name: 'Unsupported',
    className: 'bg-chariot-status-low border-chariot-stroke-low',
  },
  error: {
    name: 'Error',
    className: 'bg-chariot-status-critical border-chariot-stroke-critical',
  },
  mismatch: {
    name: 'Mismatch',
    className: 'bg-chariot-status-warning border-chariot-stroke-warning',
  },
  nomatch: {
    name: 'No Match',
    className: 'bg-chariot-status-critical border-chariot-stroke-critical',
  },
  present: {
    name: 'Present',
    className: 'bg-chariot-status-info border-chariot-stroke-info',
  },
  notpresent: {
    name: 'Not Present',
    className: 'bg-chariot-status-critical border-chariot-stroke-critical',
  },
};

function CopyTag(props: { label: string }) {
  return (
    <CopyToClipboard
      alwaysShowCopyIcon
      className="border-chariot-stroke [&_svg]:text-chariot-text-secondary flex gap-2 rounded-full border border-dashed px-3 py-1 text-xs font-medium [&_svg]:text-[12px]"
    >
      {props.label}
    </CopyToClipboard>
  );
}

function H2(props: {
  label: string;
  children?: ReactNode;
  headingElement?: ReactNode;
}) {
  return (
    <div className="z-10 flex w-full flex-col">
      <div className="bg-chariot-background-secondary flex items-center gap-2 rounded-md p-3">
        <h2 className="text-md font-bold text-nowrap">{props.label}</h2>
        {props.headingElement}
      </div>

      {props.children && (
        <div className="[&>div:not(:last-child)]:border-chariot-stroke flex flex-col [&>div]:py-4 [&>div:last-child]:pb-5 [&>div:not(:last-child)]:border-b">
          {props.children}
        </div>
      )}
    </div>
  );
}

function H3(props: {
  label?: string;
  subLabel?: ReactNode;
  children?: ReactNode;
  tooltip?: string;
}) {
  return (
    <div className={cn('flex flex-col gap-3')}>
      {(props.label || props.subLabel) && (
        <h3 className="flex items-center gap-2 text-sm font-bold text-nowrap">
          {props.label}
          {props.subLabel}
        </h3>
      )}
      {props.tooltip && (
        <Tooltip title={props.tooltip}>
          <InformationCircleIcon className="text-chariot-text-secondary size-4" />
        </Tooltip>
      )}
      {props.children && (
        <div className="flex flex-col gap-2">{props.children}</div>
      )}
    </div>
  );
}

function H4(props: {
  label?: string;
  children?: ReactNode;
  center?: boolean;
  tooltip?: string;
  footer?: ReactNode;
}) {
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  return (
    <div className={cn('flex gap-2', props.center && 'items-center')}>
      {props.label && (
        <h4 className="text-xs font-bold text-nowrap">
          <span>{props.label}</span>
          {props.children && ':'}
        </h4>
      )}

      {props.children && (
        <div className="flex flex-col gap-1">
          <div className="flex flex-row items-start gap-1 text-xs font-normal">
            {typeof props.children === 'string' ? (
              <MarkdownPreview source={props.children} className="text-xs" />
            ) : (
              props.children
            )}
          </div>
          {props.tooltip && (
            <button
              onClick={() => setIsDetailModalOpen(true)}
              className="text-chariot-brand-primary flex items-center text-xs font-medium hover:underline"
            >
              View Evidence
            </button>
          )}
          {props.footer}
        </div>
      )}
      {isDetailModalOpen && (
        <Modal
          open={isDetailModalOpen}
          onClose={() => setIsDetailModalOpen(false)}
          title={`${props.label} Agent Evidence`}
          size="3xl"
          hideFooter
        >
          <div
            className="prose prose-sm dark:prose-invert max-w-none"
            data-testid="modal-body"
          >
            <MarkdownPreview source={props.tooltip} />
          </div>
        </Modal>
      )}
    </div>
  );
}

function getAssetAffiliations(props: { asset: Asset }) {
  return (
    props.asset.history
      ?.filter(
        historyItem => historyItem.filePath && historyItem.affiliationVerdict
      )
      .reverse() || []
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- Utility function exported alongside components
export function getAssetAffiliationOverview(props: { asset: Asset }) {
  const affiliationHistory = getAssetAffiliations({ asset: props.asset });

  if (affiliationHistory.length > 0) {
    const lastVerdict =
      (affiliationHistory[0]
        .affiliationVerdict as unknown as AffiliationData['finalVerdict']) ||
      'uncertain';

    return {
      lastVerdict,
      on: affiliationHistory[0].updated,
      verdictParsedName:
        finalVerdictMap[
          lastVerdict.toLowerCase() as keyof typeof finalVerdictMap
        ].name || finalVerdictMap.uncertain.name,
    };
  }
  return undefined;
}

function List(props: {
  options: { value: string; originator: string; label?: string }[];
  label?: string;
  onItemClick?: (originator: string, value: string) => void;
}) {
  const itemsToShowInitially = DEFAULT_LIST_ITEM_LIMIT;
  const totalItems = props.options.length;
  const [expandList, setExpandList] = useState(false);

  return (
    <>
      <H4 center label={props.label}></H4>
      {totalItems === 0 ? (
        <H4 center>
          <div className="my-3 ml-2 flex items-center gap-2">
            <div className="bg-chariot-stroke inline-flex size-1.5 items-center justify-center rounded-full"></div>
            <span className="text-chariot-text-primary italic">
              No {props.label?.toLowerCase() || 'items'} found.
            </span>
          </div>
        </H4>
      ) : (
        <ul className={cn('flex flex-col gap-2', !props.onItemClick && 'mt-2')}>
          {props.options
            .slice(0, expandList ? totalItems : itemsToShowInitially)
            .map((option, index) => {
              const isClickable = !!(props.onItemClick && option.originator);
              return (
                <div
                  key={index}
                  className={
                    isClickable
                      ? 'cursor-pointer hover:shadow-[0_0_6px_2px_rgba(0,0,0,0.35)] dark:hover:shadow-[0_0_6px_2px_rgba(255,255,255,0.35)]'
                      : ''
                  }
                  onClick={
                    isClickable
                      ? () =>
                          props.onItemClick!(option.originator!, option.value)
                      : undefined
                  }
                >
                  <H4>
                    <div className="flex items-center gap-2 pl-2">
                      <div
                        className={`bg-chariot-stroke size-1.5 rounded-full`}
                      ></div>
                      <button
                        className={`${
                          isClickable
                            ? 'text-chariot-brand-primary text-sm font-bold'
                            : 'text-chariot-text-secondary'
                        }`}
                      >
                        {option.value}
                      </button>
                      {option.originator ? (
                        <CopyTag label={option.originator} />
                      ) : (
                        <span>-</span>
                      )}
                    </div>
                  </H4>
                </div>
              );
            })}
        </ul>
      )}
      {totalItems > itemsToShowInitially && totalItems > 0 && (
        <Button
          className="mt-2 w-fit rounded-full"
          onClick={() => setExpandList(!expandList)}
          variant="outline"
          size="sm"
        >
          {expandList
            ? 'Show Less'
            : `Load ${totalItems - itemsToShowInitially} More`}
        </Button>
      )}
    </>
  );
}
